// React Scan
import { scan } from 'react-scan';

import { useState, useEffect, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { RouterProvider, Navigate } from 'react-router-dom';
import { UserProfileContext, ThemeContext } from './contexts';
import { parsePolicy, decideEnrolment, graceDaysLeft } from './utilities/mfaPolicy';
import { supabase } from './utilities/supabase';
import {
  LogoLoader,
  NotificationProvider,
  AiProvider,
  useNotification
} from '@core/components';
import MaintenancePage from './pages/error/Maintenance';
import { ChatPanelProvider } from './components/chat/ChatPanelContext';
import {
  initializeSentry,
  Sentry,
  sentryCreateBrowserRouter
} from './utilities/sentry';
import UserProfile from './models/UserProfile';

// Lazy load components
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Login = lazy(() => import('./pages/auth/login/Login'));
const Signup = lazy(() => import('./pages/auth/signup/Signup'));
const ForgotPassword = lazy(() =>
  import('./pages/auth/forgotPassword/ForgotPassword')
);
const ResetPassword = lazy(() =>
  import('./pages/auth/resetPassword/ResetPassword')
);
const Loading = lazy(() => import('./pages/auth/loading/Loading'));
const StylePages = lazy(() => import('./pages/styles/Styles'));
const Error = lazy(() => import('./pages/error/Error'));
const MFA = lazy(() => import('./pages/auth/2fa/2FA'));
const MfaEnrolmentWall = lazy(() => import('./pages/auth/2fa/MfaEnrolmentWall'));
const XeroCallback = lazy(() =>
  import('./pages/dashboard/settings/tabs/integrations/XeroCallback')
);
const SignupConnect = lazy(() => import('./pages/auth/signup/SignupConnect'));
const Delegate = lazy(() => import('./pages/forms/delegate/Delegate'));
const Proposal = lazy(() => import('./pages/proposal/Proposal'));

// Re-exported for backwards compatibility — these are defined in ./contexts so
// their identity is stable under Fast Refresh. Import them from either place.
export { UserProfileContext, ThemeContext };

// A deactivated profile (Settings -> Team Members) is never chosen as the current
// profile. Returns the selected (active) usable profile, else the first usable one,
// else null.
const chooseProfile = profiles => {
  const usable = (profiles || []).filter(profile => !profile.deactivatedAt);
  return usable.find(profile => profile.active) || usable[0] || null;
};

// Every profile on this login has been deactivated: their access was removed.
const allDeactivated = profiles =>
  Boolean(profiles?.length) && profiles.every(profile => profile.deactivatedAt);

const ACCESS_REMOVED_ID = 'access-removed';

// Shown after signing out a login whose profiles are all deactivated. Lives inside
// NotificationProvider, which App itself renders, so it can use the toast.
const AccessRemovedNotice = ({ show, onShown }) => {
  const { addNotification } = useNotification();
  useEffect(() => {
    if (!show) return;
    addNotification({
      id: ACCESS_REMOVED_ID,
      type: 'error',
      title: 'Access removed',
      description: UserProfile.ACCESS_REMOVED_MESSAGE,
      duration: 15000
    });
    onShown();
  }, [show, addNotification, onShown]);
  return null;
};
AccessRemovedNotice.propTypes = {
  show: PropTypes.bool.isRequired,
  onShown: PropTypes.func.isRequired
};

const App = () => {
  const [sessionState, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [theme, setTheme] = useState('light');
  const [isBackendAvailable, setIsBackendAvailable] = useState(true);
  const [showMFA, setShowMFA] = useState(false);
  // Org-policy enrolment gate (for users without a factor): 'off'|'grace'|'enroll'
  const [mfaEnrolment, setMfaEnrolment] = useState('off');
  const [mfaGraceSkipped, setMfaGraceSkipped] = useState(false);
  const [mfaPolicy, setMfaPolicy] = useState(null);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  // Set when a login is signed out because all its profiles are deactivated.
  const [accessRemoved, setAccessRemoved] = useState(false);

  // Enable performance and dev tools
  if (window.location.hostname.includes('portal.bohsa.co.uk')) {
    initializeSentry({
      enableFeedback: true,
      enableReplays: true,
      enableTracing: true,
      enableProfiling: true
    });
  } else {
    // Enable react-scan profiling for dev
    useEffect(() => {
      scan({ enabled: process.env.NODE_ENV === 'development' });
    }, []);
    //Uncomment to enable sentry!
    // initializeSentry({
    //   enableFeedback: true
    // });
  }

  //Google apis
  useEffect(() => {
    // Load Google Maps JavaScript API
    const script = document.createElement('script');
    script.text = `(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
      key: "${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}",
      v: "weekly"
    });`;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const checkBackendServices = async () => {
    try {
      const { error } = await supabase
        .from('core_entities')
        .select('id')
        .limit(1);
      setIsBackendAvailable(!error);
    } catch (error) {
      console.error('Backend services are unavailable:', error);
      setIsBackendAvailable(false);
    }
  };

  // Re-evaluate the MFA gates for the current session. Runs on EVERY entry path
  // — live auth events and session restore on reload — otherwise an enrolled
  // aal1 session could bypass the challenge simply by refreshing.
  //  - showMFA: an enrolled user (nextLevel aal2) at aal1 must step up.
  //  - mfaEnrolment: a user WITHOUT a factor may be required to enrol by the
  //    org policy ('grace' = skippable nudge, 'enroll' = forced wall).
  const evaluateMfaGate = async profile => {
    try {
      const {
        data: { currentLevel, nextLevel }
      } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      const enrolled = nextLevel === 'aal2';
      setShowMFA(enrolled && currentLevel === 'aal1');

      // Enforcement only ever applies to users without a verified factor.
      if (enrolled) {
        setMfaEnrolment('off');
        return;
      }

      const { data: row } = await supabase
        .from('settings')
        .select('value')
        .eq('name', 'mfa_policy')
        .order('id')
        .limit(1)
        .maybeSingle();
      const policy = parsePolicy(row?.value);
      setMfaPolicy(policy);
      setMfaEnrolment(
        decideEnrolment({
          policy,
          profileId: profile?.id,
          // Enforcement only ever impacts active BOHSA admins.
          isActiveAdmin: !!(profile?.bohsaAdmin && profile?.active),
          hasVerifiedFactor: false,
          now: Date.now()
        })
      );
    } catch (error) {
      console.error('Error evaluating MFA gate:', error);
      // Fail open: never strand a user over a transient error.
      setShowMFA(false);
      setMfaEnrolment('off');
    }
  };

  // Every profile on this login is deactivated (Settings -> Team Members). The
  // deactivation bans the login and ends its sessions, but an access token issued
  // before it stays valid for up to an hour, so sign this browser out (locally: the
  // server sessions are already gone) and say why.
  const signOutRemovedAccess = async () => {
    setAccessRemoved(true);
    setUserProfile(null);
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error('Error signing out a deactivated profile:', error);
    }
    setSession(null);
  };

  const handleAuthChange = (event, session) => {
    //console.log(event);
    if (
      event === 'SIGNED_IN' ||
      event === 'SIGNED_OUT' ||
      event === 'MFA_CHALLENGE_VERIFIED' ||
      event === 'PASSWORD_RECOVERY'
    ) {
      setSession(session);
      if (event === 'SIGNED_IN' || event === 'MFA_CHALLENGE_VERIFIED') {
        if (
          !sessionState ||
          session.access_token != sessionState.access_token
        ) {
          UserProfile.getByAuthId(session.user.id).then(
            async ({ data, error }) => {
              if (data) {
                if (allDeactivated(data)) {
                  await signOutRemovedAccess();
                  return;
                }
                // Skips deactivated profiles; falls back to data[0] (as before)
                // only when the login has no profile at all.
                const activeProfile = chooseProfile(data) || data[0];

                // Safety check, if they're not active we should mark them as such
                if (activeProfile.active == false) {
                  activeProfile.active = true;
                  await activeProfile.update();
                }
                // Resolve the MFA gates BEFORE exposing the profile so an
                // enrolled (aal1) user routes straight to /mfa (or a required
                // user to the enrolment wall) rather than briefly flashing the
                // dashboard. Runs in the promise continuation, after the
                // auth-listener lock has been released.
                await evaluateMfaGate(activeProfile);
                setUserProfile(activeProfile);
                Sentry.setUser({
                  email: activeProfile.email,
                  fullName: activeProfile.fullName
                });
              } else if (error) {
                console.error('Error fetching user profile:', error);
              }
            }
          );
        }
      } else if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      } else {
        // SIGNED_OUT — clear the MFA gates so the next login starts clean.
        setUserProfile(null);
        setShowMFA(false);
        setMfaEnrolment('off');
        setMfaGraceSkipped(false);
        setPasswordRecovery(false);
        //redirect('/login');
      }
    }
  };

  // Subscribe to auth state changes and handle them via handleAuthChange callback
  // Unsubscribes when component unmounts
  useEffect(() => {
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(handleAuthChange);
    return () => subscription.unsubscribe();
  }, [handleAuthChange]);

  // Check and initialize session on app load
  // - Verifies backend services are available
  // - Gets current session if it exists
  // - Loads user profile data and preferences
  // - Updates Sentry user context
  // - Sets initial theme
  useEffect(() => {
    const checkSession = async () => {
      // First check if backend is available
      await checkBackendServices();
      if (!isBackendAvailable) return;

      // Get current session
      const {
        data: { session }
      } = await supabase.auth.getSession();
      setSession(session);

      // If session exists, load user profile data
      if (session) {
        const { data, error } = await UserProfile.getByAuthId(session.user.id);
        if (data && allDeactivated(data)) {
          await signOutRemovedAccess();
        } else if (data) {
          // Get active profile or fallback to first profile, never a deactivated one
          const activeProfile = chooseProfile(data) || data[0];
          setUserProfile(activeProfile);

          // Set up Sentry user tracking
          Sentry.setUser({
            email: activeProfile.email,
            fullName: activeProfile.fullName
          });

          // Set theme from user preferences or default to light
          setTheme(activeProfile?.crmPreferences?.theme || 'light');

          // Evaluate the MFA gates on session restore (page reload). Awaited
          // before setIsLoading(false) below so an enrolled aal1 session is
          // redirected to /mfa (or a required user to the enrolment wall)
          // rather than flashing the dashboard — closing the reload bypass.
          await evaluateMfaGate(activeProfile);
        } else if (error) {
          console.error('Error fetching user profile:', error);
        }
      }

      setIsLoading(false);
    };

    checkSession();
  }, [isBackendAvailable]);

  const handleAiRequest = async ({
    template,
    selectedText,
    customPrompt,
    customFileData
  }) => {
    try {
      // Your app-specific AI implementation here
      const { data: response } = await supabase.functions.invoke(
        'integrations/add-ai/text-completion',
        {
          body: {
            model: {
              provider: 1,
              id: 31 // gpt-4.1-nano (latest and cheapest as of 17/04/2025)
            },
            details: {
              template: template
            },
            input: {
              content: selectedText,
              prompt: customPrompt
            },
            ...(customFileData && {
              file: {
                name: customFileData.name,
                data: customFileData.data
              }
            })
          }
        }
      );

      return response;
    } catch (error) {
      console.error('Error in AI request:', error);
      throw error;
    }
  };

  const handleVoiceRequest = async audioBlob => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.webm');

    try {
      const { data: response, error } = await supabase.functions.invoke(
        'integrations/add-ai/voice-completion',
        {
          body: formData
        }
      );

      if (error) {
        console.error('Error uploading audio:', error);
        return null;
      }

      return response;
    } catch (error) {
      console.error('Error uploading audio:', error);
      return null;
    }
  };

  if (isLoading) return <LogoLoader logo={'/logo-star.png'} />;
  if (showLoadingScreen) return <Loading />;
  if (!isBackendAvailable) return <MaintenancePage />;

  // Password recovery: the reset page establishes its own short-lived recovery
  // session (via exchangeCodeForSession). Let /resetPassword render regardless
  // of session/profile state so it is never blocked by the loader below nor
  // bounced away. Gated on the PATH, not the auth event — the manual exchange
  // may surface as SIGNED_IN or PASSWORD_RECOVERY, so we don't rely on either.
  // (The old `if (passwordRecovery) return <Navigate.../>` here was dead: it
  // sits outside the RouterProvider and would throw if it ever ran.)
  const inPasswordRecovery =
    passwordRecovery ||
    window.location.pathname.replace(/\/+$/, '') === '/resetPassword';

  // During sign-in there is a brief window where the session exists but the
  // profile hasn't loaded yet. Hold on a loader rather than render the
  // authenticated app shell — layout components (e.g. HistoryDrawer) read the
  // current user from context and would crash on null.
  if (sessionState && !userProfile && !inPasswordRecovery)
    return <LogoLoader logo={'/logo-star.png'} />;

  // A required user without a factor: forced wall, or a skippable grace nudge
  // (until they skip it this session).
  const needsEnrolment =
    mfaEnrolment === 'enroll' || (mfaEnrolment === 'grace' && !mfaGraceSkipped);

  // The authenticated app shell, behind the session + MFA gate. Shared by the
  // two routes that render it ('/' via index, and every deeper path via '/*')
  // so the gate can never drift between them. Both are required: the parent
  // '/' route outranks the splat and matches '/' on its own, so without the
  // index route '/' would match the parent alone and render an empty Outlet.
  const dashboardElement = !sessionState ? (
    <Navigate to='/login' />
  ) : showMFA ? (
    <Navigate to='/mfa' />
  ) : needsEnrolment ? (
    <Navigate to='/mfa-enrolment' />
  ) : (
    <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
      <Dashboard />
    </Suspense>
  );

  const router = sentryCreateBrowserRouter([
    {
      path: '/',
      errorElement: (
        <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
          <Error />
        </Suspense>
      ),
      children: [
        {
          path: 'connect-xero',
          element: sessionState ? (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <XeroCallback />
            </Suspense>
          ) : (
            <Navigate to='/login' />
          )
        },
        {
          index: true,
          element: dashboardElement
        },
        {
          path: 'login',
          element: !sessionState ? (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Login />
            </Suspense>
          ) : showMFA ? (
            <Navigate to='/mfa' />
          ) : needsEnrolment ? (
            <Navigate to='/mfa-enrolment' />
          ) : (
            <Navigate to='/' />
          )
        },
        {
          path: 'mfa',
          element: !sessionState ? (
            <Navigate to='/login' />
          ) : !showMFA ? (
            <Navigate to='/' />
          ) : (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <MFA />
            </Suspense>
          )
        },
        {
          path: 'mfa-enrolment',
          element: !sessionState ? (
            <Navigate to='/login' />
          ) : showMFA ? (
            <Navigate to='/mfa' />
          ) : !needsEnrolment ? (
            <Navigate to='/' />
          ) : (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <MfaEnrolmentWall
                mode={mfaEnrolment}
                daysLeft={graceDaysLeft(mfaPolicy)}
                onSkip={() => setMfaGraceSkipped(true)}
                onComplete={() => evaluateMfaGate(userProfile)}
              />
            </Suspense>
          )
        },
        {
          path: 'password',
          element: !sessionState ? (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <ForgotPassword />
            </Suspense>
          ) : (
            <Navigate to='/' />
          )
        },
        {
          // Rendered unconditionally: the recovery session created mid-flow
          // would otherwise flip a session guard and bounce the form away.
          // ResetPassword handles the verifying / ready / invalid states itself.
          path: 'resetPassword',
          element: (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <ResetPassword />
            </Suspense>
          )
        },
        {
          path: 'signup',
          element: !sessionState ? (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Signup />
            </Suspense>
          ) : (
            <Navigate to='/' />
          )
        },
        {
          path: 'signup/:id',
          element: (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <SignupConnect session={sessionState} />
            </Suspense>
          )
        },
        {
          path: 'styles',
          element: !sessionState ? (
            <Navigate to='/login' />
          ) : showMFA ? (
            <Navigate to='/mfa' />
          ) : needsEnrolment ? (
            <Navigate to='/mfa-enrolment' />
          ) : (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <StylePages />
            </Suspense>
          )
        },
        {
          path: 'delegate/:formType/:spaceId',
          element: (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Delegate />
            </Suspense>
          )
        },
        {
          path: 'proposal/:accessToken',
          element: (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Proposal />
            </Suspense>
          )
        },
        {
          path: '/*',
          element: dashboardElement
        }
      ]
    }
  ]);

  return (
    <UserProfileContext.Provider value={{ userProfile, setUserProfile }}>
      <NotificationProvider>
        <AccessRemovedNotice
          show={accessRemoved}
          onShown={() => setAccessRemoved(false)}
        />
        <ThemeContext.Provider value={{ theme, setTheme }}>
          <AiProvider
            handleAiRequest={handleAiRequest}
            handleVoiceRequest={handleVoiceRequest}
          >
            <ChatPanelProvider>
              <RouterProvider router={router} />
            </ChatPanelProvider>
          </AiProvider>
        </ThemeContext.Provider>
      </NotificationProvider>
    </UserProfileContext.Provider>
  );
};

export default App;
